import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
  takeUntil,
  finalize,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import {
  ICreateServiceTemplateRequest,
  IServiceTemplate,
} from '../../../../core/models/service-template/service-template.model';
import { IServiceCategory } from '../../../../core/models/service-category.model';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { IService } from '../../../../core/models/service.model';
import {
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
} from '@irembo-andela/irembogov3-common';
import { HttpErrorResponse } from '@angular/common/http';
import { IIremboOrganization } from '../../../../core/models/irembo-organization.model';

@Component({
  selector: 'irembogov-convert-existing-service',
  templateUrl: './convert-existing-service.component.html',
  styleUrls: ['./convert-existing-service.component.scss'],
})
export class ConvertExistingServiceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  // Organization selection
  organizations: IIremboOrganization[] = [];
  selectedOrganization: IIremboOrganization | null = null;
  isLoadingOrganizations = false;

  // Service selection
  isLoading = false;
  isConverting = false;
  isNavigating = false;
  selectedService: IService | null = null;
  createdTemplateId: string | null = null;
  createdTemplateName: string | null = null;

  // Form for template details (simplified)
  categories: IServiceCategory[] = [];
  services: IService[] = [];

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;
  searchTerm = '';
  selectedCategory: string | null = '';

  // Modal states
  showTemplateCreationModal = false;

  constructor(
    private router: Router,
    private serviceTemplateService: ServiceTemplateService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadCategories();
    this.setupSearchDebouncing();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject$.complete();
  }

  private setupSearchDebouncing(): void {
    this.searchSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 0;
        this.loadServices();
      });
  }

  private loadOrganizations(): void {
    this.isLoadingOrganizations = true;

    this.serviceChangeRequestService
      .getIremboOrganizations('page=0&size=100&status=ACTIVE')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingOrganizations = false))
      )
      .subscribe({
        next: response => {
          this.organizations = response.data?.content || [];
          // Group by organizationId to avoid duplicates
          const uniqueOrgs = this.organizations.reduce((acc, organization) => {
            const existingOrg = acc.find(o => o.id === organization.id);
            if (!existingOrg) {
              acc.push(organization);
            }
            return acc;
          }, [] as IIremboOrganization[]);
          this.organizations = uniqueOrgs;
        },
        error: error => {
          const errorMessage = ExtractHttpErrorResponseCodeAndMessage(
            error,
            'Failed to load organizations'
          );
          this.toastService.show({
            body: errorMessage.message,
            type: 'error',
          });
        },
      });
  }

  private loadCategories(): void {
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.categories = response.data || [];
        },
        error: () => {
          this.categories = [];
        },
      });
  }

  onOrganizationChange(organization: any): void {
    const orgId = organization.target
      ? organization.target.value
      : organization;
    this.selectedOrganization = this.findOrganizationById(orgId);
    this.services = [];
    this.currentPage = 0;
    this.totalElements = 0;
    this.totalPages = 0;
    this.selectedCategory = '';

    if (orgId) {
      this.loadServices();
    }
  }

  loadServices(): void {
    if (!this.selectedOrganization) {
      return;
    }

    this.isLoading = true;

    this.serviceChangeRequestService
      .getServicesByOrganization(
        this.selectedOrganization.id,
        this.currentPage,
        this.pageSize,
        this.searchTerm.trim() || undefined,
        this.selectedCategory || undefined
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => {
          this.services = response.data.content || [];
          this.totalElements = response.data.totalElements || 0;
          this.totalPages = response.data.totalPages || 0;
          this.isLoading = false;
        },
        error: error => {
          const errorMessage = ExtractHttpErrorResponseCodeAndMessage(
            error,
            'Failed to load services'
          );
          this.toastService.show({
            body: errorMessage.message,
            type: 'error',
          });
          this.services = [];
          this.isLoading = false;
        },
      });
  }

  onCategoryChange(categoryId: string | null): void {
    this.selectedCategory = categoryId;
    this.currentPage = 0;
    this.loadServices();
  }

  onSearchChange(): void {
    this.searchSubject$.next(this.searchTerm);
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage) {
      this.currentPage = page;
      this.loadServices();
    }
  }

  convertToTemplate(service: IService): void {
    this.selectedService = service;
    this.isConverting = true;

    this.serviceTemplateService
      .convertServiceToTemplate(service.changeRequestId as string)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isConverting = false))
      )
      .subscribe({
        next: (response: any) => {
          this.createdTemplateId = response.data?.id || null;
          this.createdTemplateName = response.data?.name || null;
          this.showTemplateCreationModal = true;
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to convert service to template'
            );
          this.toastService.show({
            body: errorCodeAndMessage.message,
            type: 'error',
          });
          this.selectedService = null;
        },
      });
  }

  continueConfiguration(): void {
    if (!this.createdTemplateId) {
      // Fallback to templates list if no ID
      this.router.navigate(['/services/templates']);
      return;
    }

    this.isNavigating = true;

    // Navigate to edit the newly created template
    setTimeout(() => {
      this.router.navigate([
        '/services/templates',
        this.createdTemplateId,
        'edit',
      ]);
    }, 100);
  }

  cancelConversion(): void {
    this.showTemplateCreationModal = false;
    this.selectedService = null;
    this.createdTemplateId = null;
    this.createdTemplateName = null;
    this.isNavigating = false;
  }

  goBack(): void {
    this.router.navigate(['/services/templates']);
  }

  getOrganizationDisplayName(org: IIremboOrganization): string {
    return `${org.name}`;
  }

  trackByOrganizationId(index: number, org: IIremboOrganization): string {
    return org.id;
  }

  findOrganizationById(id: string | null): IIremboOrganization | null {
    return this.organizations.find(org => org.id === id) || null;
  }
}
